
import type { ServerWebExchange } from '../../../types/web/server';
import type { WebErrorHandler, WebFilter, WebFilterChain, WebHandler } from './types.ts';

export class WebHandlerDecorator implements WebHandler {
    readonly #delegate: WebHandler;

    constructor(delegate: WebHandler) {
        if (delegate === null || delegate === undefined) throw new Error('delegate must not be null or undefined');
        this.#delegate = delegate;
    }

    get delegate(): WebHandler {
        return this.#delegate;
    }

    async handle(exchange: ServerWebExchange): Promise<void> {
        return await this.#delegate.handle(exchange);
    }

    toString(): string {
        return `WebHandlerDecorator[delegate=${this.#delegate}]`;
    }
}

export class ErrorHandlingWebHandler extends WebHandlerDecorator {
    readonly #errorHandlers: Array<WebErrorHandler>;
    constructor(delegate: WebHandler, ...errorHandlers: Array<WebErrorHandler>) {
        super(delegate);
        this.#errorHandlers = [... (errorHandlers)];
        Object.freeze(this.#errorHandlers);
    }

    get errorHandlers(): ReadonlyArray<WebErrorHandler> {
        return this.#errorHandlers;
    }

    handle(exchange: ServerWebExchange): Promise<void> {
        let completion: Promise<void>;
        try {
            completion = super.handle(exchange);
        }
        catch (e) {
            completion = Promise.reject(e);
        }

        // set handled web error attribute
        //exchange.attribute()
        for (const handler of this.#errorHandlers) {
            completion = completion.catch(async (reason) => {
                const error = reason instanceof Error ? reason : new Error(String(reason));
                await handler.handle(exchange, error);
            });
        }
        return completion;
    }
}

export class DefaultWebFilterChain implements WebFilterChain {
    readonly #filters: WebFilter[];
    readonly #handler: WebHandler;

    /*readonly */ #current?: WebFilter;
    /*readonly */ #chian?: DefaultWebFilterChain;

    static of(handler: WebHandler, ...filters: WebFilter[]) {
        const allFilters = [...filters];
        const result = new DefaultWebFilterChain(filters, handler);
        const chain = DefaultWebFilterChain.initChain(filters, handler);
        result.#current = chain.#current;
        result.#chian = chain.#chian;
        return result;
    }

    private static initChain(filters: WebFilter[], handler: WebHandler): DefaultWebFilterChain {
        let chain = new DefaultWebFilterChain(filters, handler, undefined, undefined);
        for (const current of [...filters].reverse()) {
            chain = new DefaultWebFilterChain(filters, handler, current, chain);
        }
        return chain;
    }

    private constructor(filters: WebFilter[], handler: WebHandler, current? : WebFilter, chain?: DefaultWebFilterChain) {
        this.#filters = filters;
        this.#handler = handler;
        this.#current = current;
        this.#chian = chain;
    }

    get filters(): ReadonlyArray<WebFilter> {
        return this.#filters;
    }

    get handler(): WebHandler {
        return this.#handler;
    }

    async filter(exchange: ServerWebExchange): Promise<void> {
        if (this.#current !== undefined && this.#chian !== undefined) {
            await this.#invokeFilter(this.#current, this.#chian, exchange);
        }
        else {
            await this.#handler.handle(exchange);
        }
    }

    #invokeFilter(filter: WebFilter, chain: DefaultWebFilterChain, exchange: ServerWebExchange): Promise<void> {
        return filter.filter(exchange, chain);
    }
}

export class FilteringWebHandler extends WebHandlerDecorator {
    readonly #chain: DefaultWebFilterChain;

    constructor(handler: WebHandler, ...filters: Array<WebFilter>) {
        super(handler);
        this.#chain = DefaultWebFilterChain.of(handler, ...(filters));
    }

    get filters() {
        return this.#chain.filters;
    }

    handle(exchange: ServerWebExchange): Promise<void> {
        return this.#chain.filter(exchange);
    }
}
